import { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '../../../../routes/Routes';
import { usePlanningBreachStore } from '../../../../hooks/usePlanningBreachStore';
import { ErrorMessages } from '@types';
import { parseZodErrors, stringFieldSchema, textAreaInputSchema } from '@utils';
import { SELECT_OPTIONS_ERROR } from '../constants/errors';
import { ZodError } from 'zod';
import { ErrorSummary } from '@atoms/*';
import {
  FormTitle,
  NavigationButtons,
  RadioOptions,
  TextArea,
} from '@molecules/*';

export const ShortTermLetsResponsibility: FunctionComponent = () => {
  const navigate = useNavigate();
  const { data, update, resetSelective } = usePlanningBreachStore();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const title = 'Do you know who owns or manages the property?';
  const MAX_LENGTH = 500;
  const moreDetailsLabelText = 'Give more details ';
  const moreDetailsDescription =
    'Include their name and contact details, if you know them.';

  const options = [
    {
      id: 'Yes',
      title: 'Yes',
      labelText: 'Yes',
      isDefault: data.specialCharactarists === 'Yes',
    },
    {
      id: 'No',
      title: 'No',
      labelText: 'No',
      isDefault: data.specialCharactarists === 'No',
    },
  ];

  const handleChangeOptions = (selection: string) => {
    setErrorMessages([]);
    const updatedSelection = options.filter(
      (type) => type.labelText === selection,
    )[0].id as 'Yes' | 'No';

    if (updatedSelection) {
      update('specialCharactarists', updatedSelection);
    } else {
      resetSelective();
    }
  };

  const handleChangeWhoIsResponsible = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    update('detailsOfSpecialChars', event.currentTarget.value);
    setErrorMessages([]);
  };

  const buttonPrevious = {
    onClick: () => {
      navigate(RoutesList.PROPERTY_USAGE);
    },
  };

  const buttonNext = {
    onClick: async () => {
      try {
        stringFieldSchema(
          'specialCharactarists',
          SELECT_OPTIONS_ERROR.required.message,
        ).parse({
          specialCharactarists: data.specialCharactarists,
        });
        if (data.specialCharactarists === 'Yes') {
          textAreaInputSchema({
            fieldName: 'detailsOfSpecialChars',
            requiredMessage: 'Enter details about the owner of the property.',
            maxLength: MAX_LENGTH,
            maxLengthMessage: `Details about the owner must be ${MAX_LENGTH} characters or less.`,
          }).parse({
            detailsOfSpecialChars: data.detailsOfSpecialChars,
          });
        }
        navigate(RoutesList.OWNER_LIVE_AT_PROP);
      } catch (error) {
        const validationErrors = parseZodErrors(error as ZodError);
        setErrorMessages(
          validationErrors.map((error: ErrorMessages) => ({
            ...error,
            path: error.path,
          })),
        );
      }
    },
  };

  return (
    <>
      <div className={' mt-6'}>
        <ErrorSummary messages={errorMessages} />
      </div>
      <FormTitle text={title} />
      <RadioOptions
        options={options}
        onChange={handleChangeOptions}
        errorPath={'specialCharactarists'}
        errorMessages={errorMessages}
      />
      {data.specialCharactarists === 'Yes' ? (
        <TextArea
          labelText={moreDetailsLabelText}
          isLabelTextTitle={false}
          description={moreDetailsDescription}
          value={data.detailsOfSpecialChars}
          onChange={handleChangeWhoIsResponsible}
          errorPath={'detailsOfSpecialChars'}
          errorMessages={errorMessages}
        />
      ) : null}
      <NavigationButtons
        buttonNext={buttonNext}
        buttonPrevious={buttonPrevious}
      />
    </>
  );
};
